/**
 * Google Identity Services (GIS) を使った partner-aware Google サインイン。
 *
 * 設計書: docs/architecture/partner-aware-google-oauth-design.md §4.2 / §4.3
 *
 * フロー:
 *   1. POST /api/auth/google/nonce {partner_code}
 *      → {nonce, expires_in, google_oauth_client_id}
 *   2. GIS スクリプトを動的に読み込み (1 度だけ)
 *   3. google.accounts.id.initialize({ client_id, callback, nonce })
 *   4. google.accounts.id.prompt() で One-Tap 表示
 *      (notDisplayed / skippedMoment は renderButton フォールバック)
 *   5. callback で credential (= ID Token) を受け取り、
 *      POST /api/auth/google/verify_id_token {id_token, partner_code, nonce, invitation_token?}
 *   6. JWT を返却
 *
 * cross-site cookie 不使用 (設計書 §6.2)。nonce は React state (closure) のみで保持。
 */

import { apiClient } from '../../services/apiClient';

const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

export type GoogleSignInResult = {
  token: string;
  is_new_user: boolean;
  redirect_to?: string;
};

type GoogleCredentialResponse = {
  credential: string;
};

type GooglePromptNotification = {
  isNotDisplayed: () => boolean;
  isSkippedMoment: () => boolean;
  isDismissedMoment: () => boolean;
  getNotDisplayedReason: () => string;
  getSkippedReason: () => string;
  getDismissedReason: () => string;
};

type GoogleAccountsId = {
  initialize: (config: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    nonce?: string;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
    use_fedcm_for_prompt?: boolean;
  }) => void;
  prompt: (listener?: (notification: GooglePromptNotification) => void) => void;
  renderButton: (
    parent: HTMLElement,
    options: {
      type?: 'standard' | 'icon';
      theme?: 'outline' | 'filled_blue' | 'filled_black';
      size?: 'large' | 'medium' | 'small';
      text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
      shape?: 'rectangular' | 'pill';
      width?: number;
      logo_alignment?: 'left' | 'center';
    },
  ) => void;
  cancel: () => void;
  disableAutoSelect: () => void;
};

type GoogleNamespace = {
  accounts: {
    id: GoogleAccountsId;
  };
};

declare global {
  interface Window {
    google?: GoogleNamespace;
  }
}

let scriptLoadPromise: Promise<void> | null = null;

function loadGisScript(): Promise<void> {
  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }
  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }
  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${GIS_SCRIPT_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error('Failed to load GIS script')),
        { once: true },
      );
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener(
      'error',
      () => reject(new Error('Failed to load GIS script')),
      { once: true },
    );
    document.head.appendChild(script);
  });
  return scriptLoadPromise;
}

type NonceResponse = {
  nonce: string;
  expires_in: number;
  google_oauth_client_id: string;
  /**
   * Phase 7-a Login CSRF 強化の補助層 (L7)。
   * docs/architecture/login-csrf-hardening.md §4.3 / §5.5
   *
   * /nonce 発行時に backend が SHA-256 ハッシュを auth_nonces に保存し、raw 値を
   * このフィールドで返す。frontend は React state (closure) で保持し、
   * /verify_id_token のボディで送り返す。
   *
   * 「同一ブラウザ拘束」は実現しない (転送可能 bearer 値)。主要防御は L5 Origin
   * 検証 + L6 CSP frame-ancestors。CBT は depth-in-defense の補助層。
   */
  client_binding_token: string;
};

/** axios エラーから backend の error メッセージを抽出する共通ヘルパ。 */
function extractServerError(rawErr: unknown): Error {
  const apiErr = rawErr as {
    response?: { data?: { error?: string; code?: string } };
    message?: string;
  };
  const serverMsg = apiErr?.response?.data?.error;
  const code = apiErr?.response?.data?.code;
  if (serverMsg) {
    const err = new Error(serverMsg);
    (err as Error & { code?: string }).code = code;
    return err;
  }
  if (rawErr instanceof Error) return rawErr;
  return new Error(String(rawErr));
}

export type SignInOptions = {
  /** partner_code (brandConfig.id を渡す) */
  partnerCode: string;
  /** 招待リンク経由の場合のトークン */
  invitationToken?: string;
  /**
   * One-Tap が表示できない (Safari ITP / FedCM 未対応など) 場合、
   * 指定要素配下に GIS の「Sign in with Google」ボタンをレンダーする。
   */
  fallbackButtonContainer?: HTMLElement;
  /**
   * フォールバックボタンの文言。GIS の renderButton `text` オプションそのまま。
   * 既定は 'continue_with' (= 「Google で続ける」)。
   * サインアップ画面では 'signup_with' (= 「Google で登録」) を渡す。
   */
  buttonText?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  /**
   * ログイン画面からの認証か、新規登録画面からの認証かの申告。
   * 計画書: docs/requirements/login-auto-provision-gating-plan.md §4.4 / §4.6
   * 省略時は /verify_id_token 側で 'signup' 扱い (後方互換)。
   */
  intent?: 'login' | 'signup';
};

/**
 * GIS One-Tap で Google サインインを実行する。Promise が JWT 等のレスポンスで resolve する。
 *
 * 通常は One-Tap がポップアップし、ユーザー操作で credential を取得。
 * One-Tap が表示できない場合は fallbackButtonContainer に renderButton する
 * (ユーザーがボタンを押すと popup が出る)。fallbackButtonContainer が無指定の場合、
 * One-Tap 不表示時は reject する。
 */
export async function signInWithGoogle(
  opts: SignInOptions,
): Promise<GoogleSignInResult> {
  const partnerCode = opts.partnerCode;
  if (!partnerCode) {
    throw new Error('partnerCode is required');
  }

  // 1. nonce + google_oauth_client_id を取得
  let nonceResp;
  try {
    nonceResp = await apiClient.post<NonceResponse>('/auth/google/nonce', {
      partner_code: partnerCode,
    });
  } catch (err) {
    throw extractServerError(err);
  }
  const {
    nonce,
    google_oauth_client_id: clientId,
    client_binding_token: clientBindingToken,
  } = nonceResp.data;
  if (!nonce || !clientId) {
    throw new Error('Nonce or Client ID missing in /nonce response');
  }

  // 2. GIS スクリプトを読込
  await loadGisScript();
  if (!window.google?.accounts?.id) {
    throw new Error('GIS script loaded but window.google.accounts.id is missing');
  }
  const gid = window.google.accounts.id;

  // 3. credential 受信 → /verify_id_token を呼ぶ Promise を作る
  return new Promise<GoogleSignInResult>((resolve, reject) => {
    let settled = false;
    const settleReject = (err: Error) => {
      if (settled) return;
      settled = true;
      try {
        gid.cancel();
      } catch {
        /* ignore */
      }
      reject(err);
    };

    gid.initialize({
      client_id: clientId,
      nonce,
      auto_select: false,
      cancel_on_tap_outside: false,
      use_fedcm_for_prompt: true,
      callback: async (response: GoogleCredentialResponse) => {
        if (settled) return;
        try {
          const verifyResp = await apiClient.post<GoogleSignInResult>(
            '/auth/google/verify_id_token',
            {
              id_token: response.credential,
              partner_code: partnerCode,
              nonce,
              client_binding_token: clientBindingToken,
              invitation_token: opts.invitationToken,
              ...(opts.intent !== undefined ? { intent: opts.intent } : {}),
            },
          );
          settled = true;
          resolve(verifyResp.data);
        } catch (err) {
          settleReject(extractServerError(err));
        }
      },
    });

    if (opts.fallbackButtonContainer) {
      // 常にフォールバックボタンを描画 (One-Tap が出ない場合に備える)。
      // 既存カスタムボタンに重ねる UI のため、container の実寸幅に合わせて
      // GIS ボタン幅を指定する (GIS の許容範囲は 200-400px)。
      const containerWidth = opts.fallbackButtonContainer.clientWidth || 0;
      const buttonWidth = Math.max(200, Math.min(400, Math.round(containerWidth)));
      try {
        gid.renderButton(opts.fallbackButtonContainer, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: opts.buttonText ?? 'continue_with',
          shape: 'rectangular',
          width: buttonWidth,
          logo_alignment: 'left',
        });
      } catch (err) {
        // renderButton が失敗してもフロー全体は止めない
        console.warn('renderButton failed:', err);
      }
    }

    gid.prompt((notification) => {
      if (settled) return;
      // One-Tap が表示できない / スキップされた場合のハンドリング
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        if (opts.fallbackButtonContainer) {
          // フォールバックボタンが描画済なのでユーザー操作待ち。reject しない。
          return;
        }
        settleReject(
          new Error(
            `Google One-Tap not displayed: ${notification.getNotDisplayedReason?.() || notification.getSkippedReason?.() || 'unknown'}`,
          ),
        );
      }
      // isDismissedMoment は「ユーザーが × で閉じた」だけ。reject せず待機継続。
    });
  });
}
